import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Breadcrumbs } from '@/components/shared/Breadcrumbs';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Plus, Edit, Trash2, Building2, Star, CheckCircle2, X, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { pricingAPI } from '@/services/api';

// Default LLC pricing plans
const defaultLLCPlans = [
  {
    id: 1,
    name: 'Basic',
    price: 99,
    turnaround: '5-7 business days',
    description: 'Essential LLC formation',
    features: [
      'LLC formation in any state',
      'Name availability check',
      'Articles of Organization',
      'Basic operating agreement',
      'EIN application assistance'
    ],
    popular: false,
    active: true
  },
  {
    id: 2,
    name: 'Premium',
    price: 249,
    turnaround: '2-3 business days',
    description: 'Most comprehensive package',
    features: [
      'Everything in Basic',
      'Registered agent service (1 year)',
      'Custom operating agreement',
      'Business banking resolution',
      'Compliance calendar',
      'Priority processing'
    ],
    popular: true,
    active: true
  },
  {
    id: 3,
    name: 'Platinum',
    price: 399,
    turnaround: '24 hours',
    description: 'Premium white-glove service',
    features: [
      'Everything in Premium',
      'Registered agent (lifetime)',
      'Business license research',
      'Tax consultation (30 min)',
      'Annual report filing (1 year)',
      'Same-day processing'
    ],
    popular: false,
    active: true
  }
];

export default function LLCPricingManagement() {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    price: 0,
    turnaround: '',
    description: '',
    features: [],
    popular: false,
    active: true
  });
  const [newFeature, setNewFeature] = useState('');

  // Load plans from backend
  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const data = await pricingAPI.getPlans('llc', false); // Get all LLC plans
      setPlans(data);
    } catch (error) {
      console.error('Error fetching LLC pricing plans:', error);
      toast.error('Failed to load LLC pricing plans');
    } finally {
      setLoading(false);
    }
  };

  const handleAddPlan = () => {
    setEditingPlan(null);
    setFormData({
      name: '',
      price: 0,
      turnaround: '',
      description: '',
      features: [],
      popular: false,
      active: true
    });
    setShowModal(true);
  };

  const handleEditPlan = (plan) => {
    setEditingPlan(plan);
    setFormData({ ...plan });
    setShowModal(true);
  };

  const handleDeletePlan = async (planId) => {
    try {
      await pricingAPI.deletePlan(planId);
      toast.success('LLC package deleted');
      fetchPlans(); // Reload plans
    } catch (error) {
      console.error('Error deleting LLC plan:', error);
      toast.error('Failed to delete LLC package');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingPlan) {
        // Update existing plan
        await pricingAPI.updatePlan(editingPlan.id, formData);
        toast.success('LLC package updated');
      } else {
        // Add new plan
        await pricingAPI.createPlan({ ...formData, plan_type: 'llc' });
        toast.success('LLC package created');
      }
      
      setShowModal(false);
      fetchPlans(); // Reload plans
    } catch (error) {
      console.error('Error saving LLC plan:', error);
      toast.error('Failed to save LLC package');
    }
  };

  const handleAddFeature = () => {
    if (newFeature.trim()) {
      setFormData({
        ...formData,
        features: [...formData.features, newFeature.trim()]
      });
      setNewFeature('');
    }
  };

  const handleRemoveFeature = (index) => {
    const updatedFeatures = formData.features.filter((_, i) => i !== index);
    setFormData({ ...formData, features: updatedFeatures });
  };

  const togglePopular = async (planId) => {
    try {
      const plan = plans.find(p => p.id === planId);
      await pricingAPI.updatePlan(planId, { popular: !plan.popular });
      fetchPlans(); // Reload plans
    } catch (error) {
      console.error('Error toggling popular:', error);
      toast.error('Failed to update package');
    }
  };

  const toggleActive = async (planId) => {
    try {
      const plan = plans.find(p => p.id === planId);
      await pricingAPI.updatePlan(planId, { active: !plan.active });
      fetchPlans(); // Reload plans
    } catch (error) {
      console.error('Error toggling active status:', error);
      toast.error('Failed to update package status');
    }
  };

  if (!user) {
    return <div>Loading...</div>;
  }

  if (loading) {
    return (
      <DashboardLayout role={user.role} title="LLC Pricing Management" user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout role={user.role} title="LLC Pricing Management" user={user}>
      <div className="space-y-6">
        <Breadcrumbs items={[
          { label: 'Admin', href: '/admin/dashboard' },
          { label: 'LLC Pricing' }
        ]} />

        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-foreground">LLC Formation Pricing</h2>
            <p className="mt-1 text-muted-foreground">
              Configure LLC formation packages and pricing
            </p>
          </div>
          <Button className="btn-professional gap-2" onClick={handleAddPlan}>
            <Plus className="h-4 w-4" />
            Add LLC Package
          </Button>
        </div>

        {/* LLC Packages Table */}
        <Card className="tax-card">
          <CardHeader>
            <CardTitle>All LLC Packages ({plans.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Package Name</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead>Turnaround</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Features</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Popular</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {plans.map((plan) => (
                  <TableRow key={plan.id}>
                    <TableCell className="font-medium">{plan.name}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Building2 className="h-4 w-4 text-primary" />
                        <span className="font-semibold">${plan.price}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm">
                        <Clock className="h-3 w-3 text-muted-foreground" />
                        {plan.turnaround}
                      </div>
                    </TableCell>
                    <TableCell className="max-w-xs truncate">{plan.description}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{plan.features.length} features</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={plan.active}
                          onCheckedChange={() => toggleActive(plan.id)}
                        />
                        <span className="text-sm">
                          {plan.active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => togglePopular(plan.id)}
                      >
                        <Star
                          className={`h-4 w-4 ${
                            plan.popular
                              ? 'fill-primary text-primary'
                              : 'text-muted-foreground'
                          }`}
                        />
                      </Button>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEditPlan(plan)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeletePlan(plan.id)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card className="tax-card border-primary/20">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <div className="rounded-lg bg-primary/10 p-3">
                <Building2 className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground">About LLC Pricing Configuration</h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  Changes to LLC packages will be reflected immediately in the LLC Formation Dashboard. 
                  Mark one package as "Popular" to highlight it. Inactive packages won't be displayed to users.
                </p>
                <p className="mt-2 text-xs text-muted-foreground">
                  <strong>Note:</strong> This is currently a frontend demo. In production, pricing data will be stored in the database.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Add/Edit Package Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingPlan ? 'Edit LLC Package' : 'Add New LLC Package'}
            </DialogTitle>
            <DialogDescription>
              Configure the details for this LLC formation package
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="name">Package Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Premium"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="price">Price ($) *</Label>
                <Input
                  id="price"
                  type="number"
                  min="0"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: Number(e.target.value) })}
                  placeholder="249"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="turnaround">Turnaround Time *</Label>
              <Input
                id="turnaround"
                value={formData.turnaround}
                onChange={(e) => setFormData({ ...formData, turnaround: e.target.value })}
                placeholder="e.g., 2-3 business days"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description *</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="e.g., Most comprehensive package"
                required
              />
            </div>

            <div className="space-y-2">
              <Label>Package Features</Label>
              <div className="space-y-3">
                <div className="flex gap-2">
                  <Input
                    value={newFeature}
                    onChange={(e) => setNewFeature(e.target.value)}
                    placeholder="Add a feature..."
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddFeature();
                      }
                    }}
                  />
                  <Button type="button" onClick={handleAddFeature}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>

                {formData.features.length > 0 && (
                  <div className="space-y-2 rounded-lg border p-3">
                    {formData.features.map((feature, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between rounded bg-muted p-2"
                      >
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="h-4 w-4 text-success" />
                          <span className="text-sm">{feature}</span>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemoveFeature(index)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center justify-between rounded-lg border p-4">
              <div>
                <Label htmlFor="popular">Mark as Popular</Label>
                <p className="text-sm text-muted-foreground">
                  This package will be highlighted in the LLC dashboard
                </p>
              </div>
              <Switch
                id="popular"
                checked={formData.popular}
                onCheckedChange={(checked) => setFormData({ ...formData, popular: checked })}
              />
            </div>

            <div className="flex items-center justify-between rounded-lg border p-4">
              <div>
                <Label htmlFor="active">Active Status</Label>
                <p className="text-sm text-muted-foreground">
                  Only active packages are displayed to users
                </p>
              </div>
              <Switch
                id="active"
                checked={formData.active}
                onCheckedChange={(checked) => setFormData({ ...formData, active: checked })}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-professional">
                {editingPlan ? 'Update Package' : 'Create Package'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
